import { Link } from "react-router-dom";
import {
  Building2, PhoneCall, BarChart3, Receipt, Shield, CheckCircle2, Calendar, ArrowRight, MapPin, Users,
} from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../../components/marketing/Seo";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import CtaBanner from "../../components/marketing/CtaBanner";
import Reveal from "../../components/marketing/Reveal";
import { BrowserFrame, FloatBadge } from "../../components/marketing/Frames";

const CAPABILITIES = [
  { icon: Building2, title: "Consolidated dashboard", body: "Every branch's jobs, revenue, and alerts on one screen — filter to any location in one click." },
  { icon: MapPin, title: "Location-aware jobs", body: "Every job carries a home branch that drives dispatch eligibility, phone lines, and P&L attribution." },
  { icon: PhoneCall, title: "Lines per location", body: "Local numbers per branch, routed to that branch's team with business-hours rules and after-hours handling." },
  { icon: Shield, title: "Branch permissions", body: "Managers get full control of their location — and zero visibility into the others. Owners see everything." },
  { icon: BarChart3, title: "Reporting both ways", body: "Consolidated company reports and per-branch comparisons from the same live job data." },
  { icon: Receipt, title: "Single billing", body: "One subscription and one invoice, no matter how many branches you run." },
];

const STEPS = [
  { n: "1", title: "Add your locations", body: "Create each branch with its address, timezone, and default settings in minutes." },
  { n: "2", title: "Assign lines & teams", body: "Give each branch its local phone line and its technicians; set manager permissions per location." },
  { n: "3", title: "Run it from one screen", body: "Dispatch across branches, watch consolidated results, and drill into any location instantly." },
];

const FAQS = [
  { q: "How does multi-location pricing work?", a: "Multi-location plans are priced by the number of locations, starting at $249/month, with one subscription covering all branches. See the pricing page or book a demo for a custom quote on larger footprints." },
  { q: "Can each branch have its own phone number?", a: "Yes — provision a local number per branch and route it to that branch's team with its own business-hours rules, voicemail, and after-hours behavior." },
  { q: "Can branch managers see other locations' data?", a: "No — location-based permissions limit managers to their own branch's jobs, customers, and financials. Owners and admins see the consolidated picture." },
  { q: "Can I move jobs and technicians between branches?", a: "Yes — jobs can be reassigned across locations when coverage demands it, and reporting always attributes revenue to the branch that ran the work." },
];

export default function MultiLocationPage() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Multi-Location", path: "/multi-location" },
  ];
  return (
    <div className="mk min-h-screen overflow-x-hidden" data-testid="multi-location-page">
      <Seo
        title="Multi-Location Field Service Software | Fieldz AI"
        description="Run every branch from one dashboard: centralized dispatch, per-location phone lines and permissions, consolidated reporting, and single billing for multi-location field service companies."
        path="/multi-location"
        image={`${SITE_URL}/marketing/og/og-multi-location.jpg`}
        jsonLd={[breadcrumbJsonLd(crumbs), faqJsonLd(FAQS)]}
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-14 md:pb-20 bg-gradient-to-b from-emerald-50/60 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wide mb-5">
              <Building2 className="w-3.5 h-3.5" /> For Multi-Location Businesses
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.08] mb-6" data-testid="ml-h1">
              Every branch. One dashboard.
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto mb-9">
              Purpose-built for locksmith chains, multi-office HVAC companies, and any field service business running 2+
              locations — centralized dispatch, per-branch phone lines and permissions, and consolidated reporting.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-5">
              <Link to="/book-demo" className="mk-btn mk-btn-primary mk-btn-lg w-full sm:w-auto" data-testid="ml-demo-btn">
                <Calendar className="w-4 h-4" /> Book a Demo
              </Link>
              <Link to="/pricing#multi-location" className="mk-btn mk-btn-outline mk-btn-lg w-full sm:w-auto" data-testid="ml-pricing-btn">
                See Multi-Location Pricing <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <p className="flex items-center justify-center gap-1.5 text-sm text-slate-500 font-medium">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Free 7-day trial available on every plan
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            <div className="rounded-3xl bg-gradient-to-tr from-emerald-50/60 via-white to-blue-50/40 p-3 sm:p-6 border border-slate-200/60">
              <BrowserFrame
                src="/marketing/fieldz-ai-multi-location-dashboard.webp"
                alt="Fieldz AI multi-location dashboard with consolidated branch reporting"
                url="fieldzai.com/dashboard"
                eager
              />
            </div>
            <FloatBadge icon={Building2} text="3 locations — all live" className="mk-float -top-4 right-2 sm:top-10 sm:-right-5" iconClassName="text-emerald-600" />
            <FloatBadge icon={Users} text="Branch managers see only their data" className="mk-float mk-float-delay -bottom-4 left-2 sm:bottom-16 sm:-left-6" />
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2] mb-4">
              Built for branches from the ground up
            </h2>
            <p className="text-lg text-slate-600">
              Multi-location isn't a bolt-on tier — locations flow through jobs, phones, permissions, and reports.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c.title} delay={Math.min((i % 3) + 1, 3)}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_32px_-12px_rgba(15,23,42,0.14)]">
                  <div className="w-11 h-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
                    <c.icon className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-[16px] font-semibold text-slate-900 mb-2">{c.title}</h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-20 bg-[var(--mk-bg-3)] border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-[1.2]">
              Up and running in three steps
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 gap-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={Math.min(i + 1, 3)}>
                <div className="h-full rounded-2xl bg-white border border-slate-200 p-6">
                  <div className="w-10 h-10 rounded-full bg-[var(--mk-blue)] text-white font-bold flex items-center justify-center mb-4">{s.n}</div>
                  <h3 className="text-[16px] font-semibold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 text-center mb-10">
            Multi-location — frequently asked questions
          </h2>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`ml-faq-${i}`}>
                <h3 className="text-slate-900 font-semibold mb-2 text-[15.5px]">{f.q}</h3>
                <p className="text-slate-600 text-[14.5px] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="See your whole company on one screen"
        body="Book a walkthrough of multi-location dispatch, reporting, and permissions — or start a free trial today."
      />
      <MarketingFooter />
    </div>
  );
}
